import React from 'react';
import { useTranslation } from 'react-i18next';
import { User, Users } from 'lucide-react';

/** 전체 참가자 선택 시 사용하는 ID */
export const ALL_PARTICIPANTS_ID = '__all__';

interface Participant {
    id: string;
    name: string;
    department: string;
}

interface ParticipantSidebarProps {
    participants: Participant[];
    selectedParticipantId: string;
    onSelect: (id: string) => void;
    /** true이면 목록 상단에 "전체 참가자" 항목 표시 (마스터/관리자 뷰) */
    showAllParticipants?: boolean;
}

export const ParticipantSidebar: React.FC<ParticipantSidebarProps> = ({
    participants,
    selectedParticipantId,
    onSelect,
    showAllParticipants = false
}) => {
    const { t } = useTranslation('class');
    const isAllSelected = selectedParticipantId === ALL_PARTICIPANTS_ID;

    return (
        <div className="w-64 h-full bg-white border-r border-slate-200 shadow-sm flex flex-col flex-shrink-0">
            <div className="p-4 border-b border-gray-100 bg-white flex items-center justify-between flex-shrink-0">
                <h3 className="font-semibold text-gray-700">{t('shared.participants')}</h3>
                <span className="text-sm font-medium text-blue-500">
                    Total <b>{participants.length}</b>
                </span>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="divide-y divide-gray-100">
                    {showAllParticipants && (
                        <button
                            type="button"
                            onClick={() => onSelect(ALL_PARTICIPANTS_ID)}
                            className={`w-full text-left p-4 hover:bg-gray-50 transition-colors relative group ${isAllSelected ? 'bg-blue-50 hover:bg-blue-50' : ''}`}
                        >
                            {isAllSelected && (
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600" />
                            )}
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-full ${isAllSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                    <Users size={18} />
                                </div>
                                <div>
                                    <h4 className={`text-sm font-bold ${isAllSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                        {t('shared.allParticipants')}
                                    </h4>
                                </div>
                            </div>
                        </button>
                    )}
                    {participants.map((p) => {
                        const isSelected = selectedParticipantId === p.id;
                        return (
                            <button
                                key={p.id}
                                onClick={() => onSelect(p.id)}
                                className={`w-full text-left p-4 hover:bg-gray-50 transition-colors relative group ${isSelected ? 'bg-blue-50 hover:bg-blue-50' : ''}`}
                            >
                                {isSelected && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600" />
                                )}
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-full ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                        <User size={18} />
                                    </div>
                                    <div>
                                        <h4 className={`text-sm font-bold ${isSelected ? 'text-blue-900' : 'text-gray-700'}`}>
                                            {p.name}
                                        </h4>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
